import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const DISPATCH_CUTOFF_MINUTES = 13 * 60;
const DISPATCH_BUSINESS_DAYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
const melbourneTimeFormatter = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

export function getDispatchDetail(now = new Date(), blackoutDates = '', formatter = melbourneTimeFormatter) {
  const parts = {};

  for (const part of formatter.formatToParts(now)) {
    if (part.type !== 'literal') parts[part.type] = part.value;
  }

  const hour = Number(parts.hour);
  const minute = Number(parts.minute);
  const second = Number(parts.second);
  const currentMinutes = hour * 60 + minute;
  const currentDate = `${parts.year}-${parts.month}-${parts.day}`;
  const isBlackoutDate = String(blackoutDates)
    .split(/[\s,;]+/)
    .some((date) => date === currentDate);

  if (!isBlackoutDate && DISPATCH_BUSINESS_DAYS.has(parts.weekday) && currentMinutes < DISPATCH_CUTOFF_MINUTES) {
    const secondsUntilCutoff = DISPATCH_CUTOFF_MINUTES * 60 - (currentMinutes * 60 + second);
    const minutesUntilCutoff = Math.max(1, Math.ceil(secondsUntilCutoff / 60));
    const hoursRemaining = Math.floor(minutesUntilCutoff / 60);
    const minutesRemaining = minutesUntilCutoff % 60;
    const countdown = [hoursRemaining ? `${hoursRemaining}h` : '', minutesRemaining ? `${minutesRemaining}m` : '']
      .filter(Boolean)
      .join(' ');

    return `dispatch today if ordered within ${countdown}.`;
  }

  return 'dispatches next business day.';
}

class ProductInventory extends Component {
  #dispatchRefreshTimer;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#updateDispatchMessage();
    this.#scheduleDispatchRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.clearTimeout(this.#dispatchRefreshTimer);
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#updateDispatchMessage();
  };

  #scheduleDispatchRefresh = () => {
    window.clearTimeout(this.#dispatchRefreshTimer);
    const delayUntilNextMinute = 60_000 - (Date.now() % 60_000) + 50;

    this.#dispatchRefreshTimer = window.setTimeout(() => {
      this.#updateDispatchMessage();
      this.#scheduleDispatchRefresh();
    }, delayUntilNextMinute);
  };

  #updateDispatchMessage = () => {
    const detail = this.querySelector('.product-inventory__detail');
    const status = detail?.dataset.inventoryStatus;

    if (!detail || (status !== 'in_stock' && status !== 'low')) return;

    const originalDetail = detail.dataset.inventoryDetail || '';
    const dispatchTextPosition = originalDetail.search(/\b(?:dispatch(?:es)?|ship(?:s)?)\b/i);
    const prefix = dispatchTextPosition >= 0 ? originalDetail.slice(0, dispatchTextPosition) : '';

    detail.textContent = `${prefix}${getDispatchDetail(new Date(), detail.dataset.dispatchBlackoutDates)}`;
  };

  /**
   * Handles product select event by updating the inventory.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        morph(this, newInventory, { childrenOnly: true });
        this.#updateDispatchMessage();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
